import { useEffect, useState, useCallback } from 'react';
import type { WhatsNewResult, WhatsNewCatchUpEntry } from '@volley/crucible-ipc-types/ipc-channels';

/**
 * Render a highlight string that may contain `**bold**` markdown
 * spans. Splits on `**` pairs and returns an array of React nodes:
 * plain strings interleaved with `<strong>` elements.
 *
 * Only handles the `**text**` pattern — no nested syntax, no
 * escaping. Sufficient for the release-notes strings in `whats-new.ts`
 * which use bold to call out feature flags and key terms.
 */
function renderHighlight(text: string): React.ReactNode[] {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) =>
    // Odd indices are the captured groups (content inside **...**)
    i % 2 === 1 ? (
      // `text-crucible-fg` resolves to #e5e5e5 (dark) / #1a3a5c (light)
      // — theme-safe without extra CSS class overrides.
      <strong key={i} className="font-semibold text-crucible-fg">
        {part}
      </strong>
    ) : (
      part
    ),
  );
}

/**
 * "What's new" card surfaced once after Foundry auto-updates to a
 * version that ships a release-notes entry. Mounted unconditionally
 * by `<Workspace>`; the component self-suppresses when the main
 * process returns null (fresh install, no notes for this version,
 * already dismissed).
 *
 * Dismissal is durable: clicking Got it (or the close icon) calls
 * `app:dismiss-whats-new` which writes the current version into
 * `gui-settings.json`, so the card won't reappear on next launch of
 * the same version.
 *
 * **Architecture**: split into a pure presentational view
 * ({@link WhatsNewCardView}) and a stateful container
 * ({@link WhatsNewCard}). The view is SSR-renderable for tests; the
 * container handles IPC + dismissal state.
 */

export interface WhatsNewCardViewProps {
  data: WhatsNewResult;
  /** Called when the user clicks Got it OR the × close icon. */
  onDismiss: () => void;
  /** Called when the user clicks Read more (only rendered when fullNotesUrl is set). */
  onOpenFullNotes: () => void;
}

/**
 * Pure render — no IPC, no state. Given a `WhatsNewResult` and two
 * callbacks, paints the card. SSR-renderable so tests can assert
 * against the markup without needing DOM emulation.
 *
 * **Catch-up section** (issue #620): when `data.catchUp` is set, the
 * card renders a "Catch up on what you missed" section below the
 * current-version highlights, listing each skipped version's
 * headline + bullets. This covers the case where the auto-updater
 * landed a user multiple versions forward in one restart (e.g.
 * alpha.74 → alpha.78 = four skipped versions). Without this, the
 * user would only ever see the latest version's bullets and never
 * learn about the intervening features.
 */
export function WhatsNewCardView({
  data,
  onDismiss,
  onOpenFullNotes,
}: WhatsNewCardViewProps): React.JSX.Element {
  const hasCatchUp = !!data.catchUp && data.catchUp.length > 0;
  // Wider when we have a catch-up section so the list of skipped
  // versions doesn't crush the card down vertically.
  const widthClass = hasCatchUp ? 'w-[420px]' : 'w-[360px]';
  return (
    <div
      className={`fixed right-4 top-16 z-40 ${widthClass} max-h-[calc(100vh-6rem)] overflow-y-auto rounded-lg border border-crucible-accent/40 bg-crucible-bg-elevated/95 p-4 shadow-xl backdrop-blur-sm`}
      role="dialog"
      aria-labelledby="whats-new-headline"
      data-testid="whats-new-card"
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span aria-hidden="true" className="text-crucible-accent">
            ✨
          </span>
          <span className="text-xs font-medium uppercase tracking-wider text-crucible-accent-hover">
            What&apos;s new in v{data.version}
          </span>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss what's new card"
          data-testid="whats-new-close"
          className="text-white/40 transition-colors hover:text-white/80"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>

      <h2 id="whats-new-headline" className="mb-3 text-sm font-semibold text-white">
        {data.headline}
      </h2>

      {/*
       * Issue #838 — gradient card border-glow.
       * Each bullet is wrapped in `.whats-new-highlight` which
       * establishes a solid `crucible-bg-elevated` background ABOVE
       * the blurred `::before` gradient pseudo. Body text sits on
       * the solid surface — not on the hue blob — so contrast is
       * always ≥9:1 (WCAG AA requires 4.5:1). The glow spills around
       * the card edges as a decorative border-light.
       *
       * space-y-2 replaces space-y-1.5 so each card has breathing
       * room; the `text-xs` size class moves inside the card.
       */}
      <ul className="mb-4 space-y-2">
        {data.highlights.map((h, idx) => (
          // text-crucible-fg/80: theme-safe body text (#e5e5e5@80% dark,
          // #1a3a5c@80% light) — avoids white-on-white in light mode.
          <li key={idx} className="whats-new-highlight flex gap-2 text-xs text-crucible-fg/80">
            <span aria-hidden="true" className="mt-0.5 shrink-0 text-crucible-accent">
              •
            </span>
            <span>{renderHighlight(h)}</span>
          </li>
        ))}
      </ul>

      {hasCatchUp ? <CatchUpSection entries={data.catchUp ?? []} /> : null}

      <div className="flex items-center justify-between text-xs">
        <span className="text-white/30">Released {data.date}</span>
        <div className="flex items-center gap-2">
          {data.fullNotesUrl ? (
            <button
              type="button"
              onClick={onOpenFullNotes}
              data-testid="whats-new-read-more"
              className="text-crucible-accent-hover transition-colors hover:text-white hover:underline"
            >
              Read more →
            </button>
          ) : null}
          <button
            type="button"
            onClick={onDismiss}
            data-testid="whats-new-dismiss"
            className="rounded-md border border-crucible-accent/40 bg-crucible-accent/10 px-3 py-1 font-medium text-crucible-accent-hover transition-colors hover:border-crucible-accent/70 hover:bg-crucible-accent/20 hover:text-white"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Render the "Catch up on what you missed" section. Each entry is
 * a `<details>` so the user can expand the bullets they want to see
 * without the card becoming a wall of text. SSR-renderable — no
 * useState, no event handlers (native disclosure widget).
 */
function CatchUpSection({ entries }: { entries: WhatsNewCatchUpEntry[] }): React.JSX.Element {
  return (
    <div className="mb-4 border-t border-white/10 pt-3" data-testid="whats-new-catch-up">
      <div className="mb-2 text-xs font-medium uppercase tracking-wider text-white/40">
        Catch up on what you missed
      </div>
      <ul className="space-y-1.5 text-xs">
        {entries.map((entry) => (
          <li key={entry.version}>
            <details
              data-testid={`whats-new-catch-up-${entry.version}`}
              className="group rounded border border-white/5 bg-white/[0.02] px-2 py-1.5 hover:border-white/10"
            >
              {/* `[&::-webkit-details-marker]:hidden` + `marker:content-['']`
                  suppress the browser's default disclosure caret in
                  Electron/Chromium so our custom `›` chevron is the
                  only marker visible. Codex P2 catch on PR #622: a
                  bare native `<summary>` shows TWO markers side-by-
                  side. The chevron rotates 90° via group-open. */}
              <summary className="flex cursor-pointer list-none items-center gap-2 text-white/70 marker:content-[''] group-open:text-white [&::-webkit-details-marker]:hidden">
                <span
                  aria-hidden="true"
                  className="inline-block text-crucible-accent transition-transform group-open:rotate-90"
                >
                  ›
                </span>
                <span className="font-mono text-[10px] text-white/40">v{entry.version}</span>
                <span className="flex-1 truncate">{entry.headline}</span>
              </summary>
              <ul className="ml-4 mt-1.5 space-y-1 text-white/60">
                {entry.highlights.map((h, idx) => (
                  <li key={idx} className="flex gap-2">
                    <span aria-hidden="true" className="mt-0.5 text-crucible-accent/70">
                      •
                    </span>
                    {/* Bugbot round-1 MED: catch-up bullets need the same
                        renderHighlight treatment as the primary highlights —
                        whats-new.ts uses **bold** in entries across multiple
                        versions (e.g. alpha.80 headline bullets). */}
                    <span>{renderHighlight(h)}</span>
                  </li>
                ))}
              </ul>
              <div className="ml-4 mt-1 text-[10px] text-white/30">Released {entry.date}</div>
            </details>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Stateful container. Fetches once on mount (no polling), tracks
 * dismissed state locally, fires the dismiss IPC.
 */
export function WhatsNewCard(): React.JSX.Element | null {
  const [data, setData] = useState<WhatsNewResult | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await window.crucible.invoke('app:get-whats-new');
        if (!cancelled) setData(result);
      } catch {
        // Silently no-op — a missing card never breaks the workspace.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    void (async () => {
      try {
        await window.crucible.invoke('app:dismiss-whats-new');
      } catch {
        // Even if the dismiss IPC fails, keep the card hidden for
        // this session — the next launch will re-fetch and either
        // re-show (true persist failure) or stay hidden (transient).
      }
    })();
  }, []);

  const handleOpenFullNotes = useCallback(() => {
    if (data?.fullNotesUrl) {
      window.open(data.fullNotesUrl, '_blank', 'noopener,noreferrer');
    }
  }, [data]);

  if (dismissed || !data) return null;

  return (
    <WhatsNewCardView data={data} onDismiss={handleDismiss} onOpenFullNotes={handleOpenFullNotes} />
  );
}
